import { useState } from "preact/hooks";
import type { SpaceOut } from "../../api/types.gen.ts";
import { SLOT, t } from "../../i18n/index.ts";
import type { ComposerAction, WrittenAbout } from "../../lib/conversation.ts";
import { sendKeys } from "../../lib/platform.ts";
import { useAction } from "../../lib/resource.ts";
import { Button } from "../../ui/Button.tsx";
import { DismissedIcon, ExpandIcon, ResolvedIcon } from "../../ui/icons.tsx";
import { Menu } from "../../ui/Menu.tsx";
import { Slotted } from "../../ui/misc.tsx";
import { iconButtonClass } from "../../ui/styles.ts";
import { Toolbar } from "../../ui/Toolbar.tsx";
import { MentionTextarea } from "./richText.tsx";

/**
 * Writing a comment: `@` suggests members and `#` items (arrows and Enter or Tab pick; the box shows `@Name`), and
 * ⌘/Ctrl+Enter sends. Other ways to send (closing a finding with the comment as its reason) beside.
 */
export function Composer({
  space,
  placeholder,
  send,
  sendLabel,
  initial = "",
  actions = [],
  onCancel,
  autoFocus = false,
  about,
  compact = false,
}: {
  space: SpaceOut;
  /** What it's written about (an item's links are suggested first), who's in its conversation, and who else can be
   *  mentioned (besides the space's members; from outside, the only ones). */
  about: WrittenAbout;
  /** In a finding's or review's card: one line until written in, no hint. */
  compact?: boolean;
  placeholder: string;
  send: (body: string) => Promise<void>;
  sendLabel: string;
  /** A comment's text, when editing it. */
  initial?: string;
  actions?: readonly ComposerAction[] | undefined;
  onCancel?: (() => void) | undefined;
  autoFocus?: boolean;
}) {
  const [text, setText] = useState(initial);
  const [run, busy] = useAction(async (fn: (body: string) => Promise<void>) => {
    await fn(text.trim());
    setText("");
  });
  const empty = text.trim() === "";
  return (
    <div class="group/composer flex flex-col gap-2">
      <MentionTextarea
        space={space}
        value={text}
        onValue={setText}
        about={about}
        rows={compact && empty ? 1 : 2}
        placeholder={placeholder}
        label={placeholder}
        autoFocus={autoFocus}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && !empty) {
            e.preventDefault();
            run(send);
          }
          if (e.key === "Escape" && onCancel) {
            onCancel();
          }
        }}
      />
      <div class="flex">
        <Toolbar
          items={[
            {
              key: "send",
              node: (
                <Button size="sm" variant="primary" disabled={empty} busy={busy} onClick={() => run(send)}>
                  {sendLabel}
                </Button>
              ),
            },
            {
              key: "ways",
              node:
                actions.length > 0 && !empty ? (
                  <Menu
                    label={
                      <>
                        <ExpandIcon size={14} />
                        <span class="sr-only">{t("comments.moreWays")}</span>
                      </>
                    }
                    class={iconButtonClass("sm", "secondary")}
                    items={actions.map((a) => ({
                      key: a.key,
                      label: a.label,
                      icon: a.key === "resolve" ? <ResolvedIcon size={16} /> : <DismissedIcon size={16} />,
                      onSelect: () => run(a.run),
                    }))}
                  />
                ) : null,
            },
            {
              key: "cancel",
              node: onCancel ? (
                <Button size="sm" variant="ghost" onClick={onCancel}>
                  {t("common.cancel")}
                </Button>
              ) : null,
            },
            {
              key: "hint",
              // While writing (its box focused): it fades in, in its room (nothing moves). Where there's no room
              // (a narrow panel), it's not there.
              node: (
                <span class="hidden text-xs text-zinc-500 opacity-0 transition-opacity group-has-[textarea:focus]/composer:opacity-100 sm:inline">
                  {compact ? (
                    <kbd class="font-sans">{sendKeys()}</kbd>
                  ) : (
                    <Slotted text={t("comments.hint", { keys: SLOT })}>
                      <kbd class="font-sans">{sendKeys()}</kbd>
                    </Slotted>
                  )}
                </span>
              ),
              end: true,
              drop: 10,
              menu: [],
            },
          ]}
        />
      </div>
    </div>
  );
}
